import React, { useEffect, useState, useRef, useCallback } from "react";
import { BackButton } from "../../../components/common/GeneralComponent";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/partials/Loader";
import { useDispatch, useSelector } from "react-redux";
import AdministrativeInfo from "./AdministrativeInfo";
import BeneficiaryDetails from "./BeneficiaryDetails";
import MicroGrantDetails from "./MicroGrantDetails";
import VerificationSupport from "./VerificationSupport";
import OverallProgress from "./OverallProgress";
import Attachments from "./Attachments";

const AddMicroGrant = () => {
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const allTabs = [
    {
      name: "Administrative Info",
      permission: "micro-grant-administrative-info-create",
    },
    {
      name: "Beneficiary Details",
      permission: "micro-grant-beneficiary-details-create",
    },
    {
      name: "Micro Grant Details",
      permission: "micro-grant-grants-details-create",
    },
    {
      name: "Verification & Support",
      permission: "micro-grant-verification-support-create",
    },
    {
      name: "Overall Progress",
      permission: "micro-grant-overall-progress-create",
    },
    {
      name: "Attachments",
      permission: "micro-grant-attachment-create",
    },
  ];

  const tabs = allTabs.filter((tab) => permissions.includes(tab.permission));

  const [activeTab, setActiveTab] = useState(tabs[0]?.name || "");
  const [completedTabs, setCompletedTabs] = useState(new Set([tabs[0]?.name]));
  const [grantId, setGrantId] = useState(null);
  const [grantData, setGrantData] = useState(null);

  const [formDataState, setFormDataState] = useState({
    administrativeInfo: {},
    beneficiaryDetails: {},
    grantDetails: {},
    verificationSupport: {},
    overallProgress: {},
  });

  const [sectionValidation, setSectionValidation] = useState({
    administrativeInfo: false,
    beneficiaryDetails: false,
    grantDetails: false,
    verificationSupport: false,
    overallProgress: false,
  });

  const [isLeftClicked, setIsLeftClicked] = useState(false);
  const [isRightClicked, setIsRightClicked] = useState(false);

  const updateGrantIdRef = useRef(null);

  useEffect(() => {
    updateGrantIdRef.current = (newGrantId) => {
      setGrantId(newGrantId);
    };
    window.updateGrantId = updateGrantIdRef.current;

    return () => {
      if (window.updateGrantId === updateGrantIdRef.current) {
        delete window.updateGrantId;
      }
      updateGrantIdRef.current = null;
    };
  }, []);

  const scrollTabs = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });

      if (direction === "left") {
        setIsLeftClicked(true);
        setTimeout(() => setIsLeftClicked(false), 200);
      } else {
        setIsRightClicked(true);
        setTimeout(() => setIsRightClicked(false), 200);
      }
    }
  };

  const handleGrantCreated = useCallback((newGrantId, data) => {
    setGrantId(newGrantId);
    setGrantData(data);
  }, []);

  const storeFormData = useCallback((section, data) => {
    setFormDataState((prev) => ({
      ...prev,
      [section]: data,
    }));
  }, []);

  const handleSectionValidation = useCallback((section, isValid) => {
    setSectionValidation((prev) => ({
      ...prev,
      [section]: isValid,
    }));
  }, []);

  const handleFinalSubmit = useCallback(() => {
    navigate("/micro-grant");
  }, [navigate]);

  const handleNext = useCallback(() => {
    const currentIndex = tabs.findIndex((t) => t.name === activeTab);
    if (currentIndex < tabs.length - 1) {
      const nextTab = tabs[currentIndex + 1].name;
      setCompletedTabs((prev) => new Set([...prev, nextTab]));
      setActiveTab(nextTab);
    }
  }, [activeTab, tabs]);

  const handleTabClick = useCallback(
    (tabName) => {
      if (completedTabs.has(tabName) || tabName === activeTab) {
        setActiveTab(tabName);
      }
    },
    [completedTabs, activeTab]
  );

  const isTabAccessible = useCallback(
    (tabName) => {
      return completedTabs.has(tabName) || tabName === activeTab;
    },
    [completedTabs, activeTab]
  );

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}

      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create Micro Grant
        </h1>
        <BackButton text="Back" to="/micro-grant" />
      </div>
      <div className="mt-6">
        <div className="flex items-center rounded-t-lg bg-[#fdf5ec] relative">
          <button
            onClick={() => scrollTabs("left")}
            className={`text-xl px-4 py-2 font-bold rounded-lg transition-colors ${
              isLeftClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            «
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-hidden scrollbar-hide"
          >
            {tabs.map((tab) => {
              const isAccessible = isTabAccessible(tab.name);
              return (
                <button
                  key={tab.name}
                  onClick={() => handleTabClick(tab.name)}
                  disabled={!isAccessible}
                  className={`px-6 py-2 font-semibold whitespace-nowrap transition-colors ${
                    activeTab === tab.name
                      ? "bg-[#caa316] text-white"
                      : isAccessible
                      ? "text-black hover:bg-gray-100"
                      : "text-gray-400 cursor-not-allowed opacity-50"
                  }`}
                  style={{
                    borderTopLeftRadius: "13px",
                    borderTopRightRadius: "30px",
                  }}
                >
                  {tab.name}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => scrollTabs("right")}
            className={`ml-auto text-xl px-4 py-2 rounded-lg font-bold transition-colors ${
              isRightClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            »
          </button>
        </div>
        <div className="bg-white rounded-b-lg shadow-md p-6">
          {activeTab === "Administrative Info" && (
            <AdministrativeInfo
              onNext={handleNext}
              grantId={grantId}
              onGrantCreated={handleGrantCreated}
              onStoreFormData={(data) =>
                storeFormData("administrativeInfo", data)
              }
              onValidationChange={(isValid) =>
                handleSectionValidation("administrativeInfo", isValid)
              }
              initialData={formDataState.administrativeInfo}
              isEditMode={false}
              showNextOnly={true}
            />
          )}

          {activeTab === "Beneficiary Details" && (
            <BeneficiaryDetails
              onNext={handleNext}
              grantId={grantId}
              onStoreFormData={(data) =>
                storeFormData("beneficiaryDetails", data)
              }
              onValidationChange={(isValid) =>
                handleSectionValidation("beneficiaryDetails", isValid)
              }
              initialData={formDataState.beneficiaryDetails}
              isEditMode={false}
              showNextOnly={true}
            />
          )}

          {activeTab === "Micro Grant Details" && (
            <MicroGrantDetails
              onNext={handleNext}
              grantId={grantId}
              onStoreFormData={(data) => storeFormData("grantDetails", data)}
              onValidationChange={(isValid) =>
                handleSectionValidation("grantDetails", isValid)
              }
              initialData={formDataState.grantDetails}
              isEditMode={false}
              showNextOnly={true}
            />
          )}

          {activeTab === "Verification & Support" && (
            <VerificationSupport
              onNext={handleNext}
              grantId={grantId}
              onStoreFormData={(data) =>
                storeFormData("verificationSupport", data)
              }
              onValidationChange={(isValid) =>
                handleSectionValidation("verificationSupport", isValid)
              }
              initialData={formDataState.verificationSupport}
              isEditMode={false}
              showNextOnly={true}
            />
          )}

          {activeTab === "Overall Progress" && (
            <OverallProgress
              onNext={handleNext}
              grantId={grantId}
              onStoreFormData={(data) => storeFormData("overallProgress", data)}
              onValidationChange={(isValid) =>
                handleSectionValidation("overallProgress", isValid)
              }
              initialData={formDataState.overallProgress}
              isEditMode={false}
              showNextOnly={true}
            />
          )}

          {activeTab === "Attachments" && (
            <Attachments
              grantId={grantId}
              onSectionSaved={handleFinalSubmit}
              isEditMode={false}
              showFinalSubmit={true}
              allFormData={formDataState}
              sectionValidation={sectionValidation}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AddMicroGrant;